/**
 * UI Store (Zustand)
 *
 * Global UI state management for:
 * - Sidebar state
 * - Modals
 * - Drawers
 * - Theme
 * - Loading states
 * - Active views
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Theme mode
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * View mode for lists
 */
export type ViewMode = 'grid' | 'list' | 'table';

/**
 * Modal state
 */
export interface ModalState {
  /** Modal ID */
  id: string;
  /** Is modal open */
  isOpen: boolean;
  /** Modal data/props */
  data?: any;
}

/**
 * Drawer state
 */
export interface DrawerState {
  /** Drawer ID */
  id: string;
  /** Is drawer open */
  isOpen: boolean;
  /** Drawer data/props */
  data?: any;
}

/**
 * Loading state for async operations
 */
export interface LoadingState {
  /** Operation ID */
  id: string;
  /** Loading message */
  message?: string;
  /** Progress (0-100) */
  progress?: number;
}

/**
 * UI Store State
 */
export interface UIState {
  // ===== SIDEBAR =====
  /** Is sidebar open */
  isSidebarOpen: boolean;
  /** Is sidebar collapsed */
  isSidebarCollapsed: boolean;

  // ===== THEME =====
  /** Current theme mode */
  theme: ThemeMode;
  /** Is dark mode active */
  isDarkMode: boolean;

  // ===== MODALS =====
  /** Active modals */
  modals: Record<string, ModalState>;

  // ===== DRAWERS =====
  /** Active drawers */
  drawers: Record<string, DrawerState>;

  // ===== LOADING =====
  /** Global loading states */
  loading: Record<string, LoadingState>;
  /** Is any operation loading */
  isLoading: boolean;

  // ===== VIEW MODES =====
  /** View mode for different sections */
  viewModes: Record<string, ViewMode>;

  // ===== ACTIVE ITEMS =====
  /** Active tab IDs by section */
  activeTabs: Record<string, string>;
  /** Active panel IDs */
  activePanels: string[];

  // ===== LAYOUT =====
  /** Is mobile view */
  isMobile: boolean;
  /** Is tablet view */
  isTablet: boolean;
  /** Screen width */
  screenWidth: number;

  // ===== ACTIONS =====
  // Sidebar actions
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebarCollapse: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Theme actions
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;

  // Modal actions
  openModal: (id: string, data?: any) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  isModalOpen: (id: string) => boolean;
  getModalData: (id: string) => any;

  // Drawer actions
  openDrawer: (id: string, data?: any) => void;
  closeDrawer: (id: string) => void;
  closeAllDrawers: () => void;
  isDrawerOpen: (id: string) => boolean;
  getDrawerData: (id: string) => any;

  // Loading actions
  startLoading: (id: string, message?: string) => void;
  stopLoading: (id: string) => void;
  updateLoadingProgress: (id: string, progress: number) => void;
  stopAllLoading: () => void;
  isOperationLoading: (id: string) => boolean;

  // View mode actions
  setViewMode: (section: string, mode: ViewMode) => void;
  getViewMode: (section: string) => ViewMode;

  // Tab actions
  setActiveTab: (section: string, tabId: string) => void;
  getActiveTab: (section: string) => string | undefined;

  // Panel actions
  togglePanel: (panelId: string) => void;
  openPanel: (panelId: string) => void;
  closePanel: (panelId: string) => void;
  isPanelOpen: (panelId: string) => boolean;

  // Layout actions
  setScreenWidth: (width: number) => void;
  updateLayoutMode: () => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  isSidebarOpen: true,
  isSidebarCollapsed: false,
  theme: 'system' as ThemeMode,
  isDarkMode: false,
  modals: {},
  drawers: {},
  loading: {},
  isLoading: false,
  viewModes: {},
  activeTabs: {},
  activePanels: [],
  isMobile: false,
  isTablet: false,
  screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
};

// ============================================================================
// STORE
// ============================================================================

/**
 * UI Store
 *
 * Global store for UI state management
 */
export const useUIStore = create<UIState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ===== SIDEBAR ACTIONS =====

      toggleSidebar: () => {
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen }), false, 'toggleSidebar');
      },

      openSidebar: () => {
        set({ isSidebarOpen: true }, false, 'openSidebar');
      },

      closeSidebar: () => {
        set({ isSidebarOpen: false }, false, 'closeSidebar');
      },

      toggleSidebarCollapse: () => {
        set(
          (state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed }),
          false,
          'toggleSidebarCollapse'
        );
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ isSidebarCollapsed: collapsed }, false, 'setSidebarCollapsed');
      },

      // ===== THEME ACTIONS =====

      setTheme: (theme: ThemeMode) => {
        const isDarkMode =
          theme === 'dark' ||
          (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

        set({ theme, isDarkMode }, false, 'setTheme');
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme = currentTheme === 'light' ? 'dark' : 'light';
        get().setTheme(newTheme);
      },

      // ===== MODAL ACTIONS =====

      openModal: (id: string, data?: any) => {
        set(
          (state) => ({
            modals: {
              ...state.modals,
              [id]: { id, isOpen: true, data },
            },
          }),
          false,
          'openModal'
        );
      },

      closeModal: (id: string) => {
        set(
          (state) => ({
            modals: {
              ...state.modals,
              [id]: { ...(state.modals[id] || { id }), isOpen: false },
            },
          }),
          false,
          'closeModal'
        );
      },

      closeAllModals: () => {
        set(
          (state) => ({
            modals: Object.keys(state.modals).reduce(
              (acc, key) => ({
                ...acc,
                [key]: { ...state.modals[key], isOpen: false },
              }),
              {}
            ),
          }),
          false,
          'closeAllModals'
        );
      },

      isModalOpen: (id: string): boolean => {
        return get().modals[id]?.isOpen || false;
      },

      getModalData: (id: string): any => {
        return get().modals[id]?.data;
      },

      // ===== DRAWER ACTIONS =====

      openDrawer: (id: string, data?: any) => {
        set(
          (state) => ({
            drawers: {
              ...state.drawers,
              [id]: { id, isOpen: true, data },
            },
          }),
          false,
          'openDrawer'
        );
      },

      closeDrawer: (id: string) => {
        set(
          (state) => ({
            drawers: {
              ...state.drawers,
              [id]: { ...(state.drawers[id] || { id }), isOpen: false },
            },
          }),
          false,
          'closeDrawer'
        );
      },

      closeAllDrawers: () => {
        set(
          (state) => ({
            drawers: Object.keys(state.drawers).reduce(
              (acc, key) => ({
                ...acc,
                [key]: { ...state.drawers[key], isOpen: false },
              }),
              {}
            ),
          }),
          false,
          'closeAllDrawers'
        );
      },

      isDrawerOpen: (id: string): boolean => {
        return get().drawers[id]?.isOpen || false;
      },

      getDrawerData: (id: string): any => {
        return get().drawers[id]?.data;
      },

      // ===== LOADING ACTIONS =====

      startLoading: (id: string, message?: string) => {
        set(
          (state) => ({
            loading: {
              ...state.loading,
              [id]: { id, message, progress: 0 },
            },
            isLoading: true,
          }),
          false,
          'startLoading'
        );
      },

      stopLoading: (id: string) => {
        set(
          (state) => {
            const { [id]: removed, ...restLoading } = state.loading;
            return {
              loading: restLoading,
              isLoading: Object.keys(restLoading).length > 0,
            };
          },
          false,
          'stopLoading'
        );
      },

      updateLoadingProgress: (id: string, progress: number) => {
        set(
          (state) => ({
            loading: {
              ...state.loading,
              [id]: { ...state.loading[id], progress },
            },
          }),
          false,
          'updateLoadingProgress'
        );
      },

      stopAllLoading: () => {
        set({ loading: {}, isLoading: false }, false, 'stopAllLoading');
      },

      isOperationLoading: (id: string): boolean => {
        return !!get().loading[id];
      },

      // ===== VIEW MODE ACTIONS =====

      setViewMode: (section: string, mode: ViewMode) => {
        set(
          (state) => ({
            viewModes: {
              ...state.viewModes,
              [section]: mode,
            },
          }),
          false,
          'setViewMode'
        );
      },

      getViewMode: (section: string): ViewMode => {
        return get().viewModes[section] || 'table';
      },

      // ===== TAB ACTIONS =====

      setActiveTab: (section: string, tabId: string) => {
        set(
          (state) => ({
            activeTabs: {
              ...state.activeTabs,
              [section]: tabId,
            },
          }),
          false,
          'setActiveTab'
        );
      },

      getActiveTab: (section: string): string | undefined => {
        return get().activeTabs[section];
      },

      // ===== PANEL ACTIONS =====

      togglePanel: (panelId: string) => {
        set(
          (state) => ({
            activePanels: state.activePanels.includes(panelId)
              ? state.activePanels.filter((id) => id !== panelId)
              : [...state.activePanels, panelId],
          }),
          false,
          'togglePanel'
        );
      },

      openPanel: (panelId: string) => {
        set(
          (state) => ({
            activePanels: state.activePanels.includes(panelId)
              ? state.activePanels
              : [...state.activePanels, panelId],
          }),
          false,
          'openPanel'
        );
      },

      closePanel: (panelId: string) => {
        set(
          (state) => ({
            activePanels: state.activePanels.filter((id) => id !== panelId),
          }),
          false,
          'closePanel'
        );
      },

      isPanelOpen: (panelId: string): boolean => {
        return get().activePanels.includes(panelId);
      },

      // ===== LAYOUT ACTIONS =====

      setScreenWidth: (width: number) => {
        set({ screenWidth: width }, false, 'setScreenWidth');
        get().updateLayoutMode();
      },

      updateLayoutMode: () => {
        const width = get().screenWidth;
        set(
          {
            isMobile: width < 768,
            isTablet: width >= 768 && width < 1024,
          },
          false,
          'updateLayoutMode'
        );
      },

      // ===== RESET =====

      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    { name: 'UIStore' }
  )
);
